import React, { useCallback, useEffect, useState } from "react";
import {
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  PieChart,
  Download,
  LayoutDashboard,
} from "lucide-react";

/* ===================== COMPONENTS ===================== */
import StatsCard from "../../components/Cards/StatsCard";
import LineChart from "../../components/Charts/LineChart";
import PieChartComponent from "../../components/Charts/PieChart"; // Renamed slightly to avoid clash
import APITable from "../../components/Tables/APITable";
import Pagination from "../../components/Pagination/Pagination";
import DateRangePicker from "../../components/Calender/DateRangePicker";
import ProductUsagePanel from "../../components/AdminComponents/ProductUsagePanel/ProductUsagePanel";
import {
  LineSkeleton,
  PieSkeleton,
  StatsSkeleton,
  TableSkeleton,
} from "../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../apiService";
import { can } from "../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./Dashboard.module.scss";

const Dashboard = () => {
  // Default to the first dashboard tab the role is allowed to see.
  const [activeTab, setActiveTab] = useState(() =>
    can("dashboard", "product-dashboard") ? "product" : "api",
  );
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statsData, setStatsData] = useState([]);
  const [errorCode] = useState("");
  const [datePickerKey] = useState(0);
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [statsLoading, setStatsLoading] = useState(true);

  const [lineGraphData, setLineGraphData] = useState([]);
  const [pieGraphData, setPieGraphData] = useState([]);
  const [graphLoading, setGraphLoading] = useState(true);

  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  const fetchDashboardStats = async () => {
    setStatsLoading(true);
    try {
      const res = await apiGet("catalog", "/api-usage-dashboard");

      if (res?.success && res.currentData?.length) {
        const current = res.currentData[0];
        const improvement = res.improvementData || {};

        setStatsData([
          {
            title: "Total API Hits",
            value: current.total_api_call ?? 0,
            icon: <Activity size={24} />,
            validationMsg: `${improvement.api_call_improvement ?? 0}% from last 30 days`,
          },
          {
            title: "Success Rate",
            value: `${current.success_rate ?? 0}%`,
            icon: <CheckCircle size={24} />,
            validationMsg: `${improvement.success_rate_improvement ?? 0}% from last 30 days`,
          },
          {
            title: "Error Rate",
            value: `${current.error_rate ?? 0}%`,
            icon: <XCircle size={24} />,
            negative: true,
            validationMsg: `${improvement.error_rate_improvement ?? 0}% from last 30 days`,
          },
          {
            title: "Avg Response Time",
            value: `${current.average_latency_ms ?? 0} ms`,
            icon: <Clock size={24} />,
            validationMsg: `${improvement.average_latency_ms_improvement ?? 0} ms from last 30 days `,
          },
        ]);
      }
    } catch (error) {
      console.error("Dashboard stats API error", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchTableData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
      };

      if (fromDate) {
        const from = new Date(fromDate);
        from.setHours(0, 0, 0, 0);
        params.from_date = from.toISOString();
      }

      if (toDate) {
        const to = new Date(toDate);
        to.setHours(23, 59, 59, 999);
        params.to_date = to.toISOString();
      }

      const query = new URLSearchParams(params).toString();
      const endpoint = `/api-usage?${query}`;
      const res = await apiGet("catalog", endpoint);

      if (res?.success) {
        setRows(res.data || []);
        setPagination({
          total: res.pagination.total,
          totalPages: res.pagination.totalPages,
        });
      }
    } catch (err) {
      console.error("Table API error", err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, fromDate, toDate]);

  const fetchGraphData = async () => {
    setGraphLoading(true);
    try {
      const res = await apiGet("catalog", "/api-usage-graph");
      setLineGraphData(res?.data?.lineGraphData || []);
      setPieGraphData(res?.data?.pieGraphData || []);
    } catch (err) {
      console.error("Graph API error", err);
    } finally {
      setGraphLoading(false);
    }
  };

  /* ===================== EFFECTS ===================== */
  /* Tab-scoped loading: the Product tab's APIs live inside ProductUsagePanel
     (fired when the panel mounts). The API tab's endpoints are fetched only
     when that tab is first activated, instead of all at once on page load. */
  const [apiTabLoaded, setApiTabLoaded] = useState(false);

  useEffect(() => {
    if (activeTab !== "api" || apiTabLoaded) return;
    setApiTabLoaded(true);
    fetchDashboardStats();
    fetchGraphData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, apiTabLoaded]);

  useEffect(() => {
    if (activeTab !== "api") return;
    fetchTableData();
  }, [activeTab, fetchTableData]);

  useEffect(() => {
    setPage(1);
  }, [errorCode, fromDate, toDate]);

  const columns = [
    { label: "Category", key: "category" },
    { label: "API Name", key: "api_name" },
    { label: "Total Hits", key: "total_hits" },
    { label: "Error Rates (%)", key: "error_rate" },
    { label: "Average Response Time (SEC)", key: "avg_response_time_sec" },
  ];

  /* ===================== DOWNLOAD (CSV) ===================== */
  const handleDownload = () => {
    const cols = columns;
    const header = cols.map((c) => c.label).join(",") + "\n";
    const body = rows
      .map((r) => cols.map((c) => r[c.key] ?? "").join(","))
      .join("\n");

    const blob = new Blob([header + body], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "api_usage.csv";
    link.click();
  };

  return (
    <div className={styles["admin-dashboard__page"]}>
      {/* HEADER */}
      <div className={styles["admin-dashboard__header"]}>
        <div className={styles["admin-dashboard__icon-wrapper"]}>
          <LayoutDashboard size={24} strokeWidth={2.5} />
        </div>
        <div className={styles["admin-dashboard__title-group"]}>
          <h2 className={styles["admin-dashboard__title"]}>Dashboard</h2>
          <p className={styles["admin-dashboard__subtitle"]}>
            Monitor your API usage and performance metrics
          </p>
        </div>
      </div>

      {/* TABS */}
      <div className={styles["admin-dashboard__tabs"]} role="tablist">
        {can("dashboard", "product-dashboard") && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "product"}
            className={`${styles["admin-dashboard__tab"]} ${
              activeTab === "product"
                ? styles["admin-dashboard__tab--active"]
                : ""
            }`}
            onClick={() => setActiveTab("product")}
          >
            Product
          </button>
        )}
        {can("dashboard", "api-dashboard") && (
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "api"}
            className={`${styles["admin-dashboard__tab"]} ${
              activeTab === "api" ? styles["admin-dashboard__tab--active"] : ""
            }`}
            onClick={() => setActiveTab("api")}
          >
            API
          </button>
        )}
      </div>

      {can("dashboard", "product-dashboard") && activeTab === "product" && (
        <ProductUsagePanel />
      )}

      {can("dashboard", "api-dashboard") && activeTab === "api" && (
        <>
          {/* STATS GRID */}
          <div className={styles["admin-dashboard__stats-grid"]}>
            {statsLoading
              ? [...Array(4)].map((_, i) => <StatsSkeleton key={i} />)
              : statsData.map((item, index) => (
                  <StatsCard key={index} {...item} />
                ))}
          </div>

          {/* CHARTS SECTION */}
          {(graphLoading ||
            lineGraphData.length > 0 ||
            pieGraphData.length > 0) && (
            <div className={styles["admin-dashboard__charts-grid"]}>
              {/* LINE CHART */}
              {(graphLoading || lineGraphData.length > 0) && (
                <div className={styles["admin-dashboard__card"]}>
                  <div className={styles["admin-dashboard__chart-header"]}>
                    <div
                      className={styles["admin-dashboard__chart-title-group"]}
                    >
                      <h3 className={styles["admin-dashboard__chart-title"]}>
                        <TrendingUp size={20} strokeWidth={2.5} />
                        API Usage Trend
                      </h3>
                      <p className={styles["admin-dashboard__chart-subtitle"]}>
                        Monthly API calls over time
                      </p>
                    </div>
                    <button className={styles["admin-dashboard__filter-btn"]}>
                      Last 6 Months
                    </button>
                  </div>
                  {graphLoading ? (
                    <LineSkeleton height={300} />
                  ) : (
                    <LineChart lineGraphData={lineGraphData} />
                  )}
                </div>
              )}

              {/* PIE CHART */}
              {(graphLoading || pieGraphData.length > 0) && (
                <div className={styles["admin-dashboard__card"]}>
                  <div className={styles["admin-dashboard__chart-header"]}>
                    <div
                      className={styles["admin-dashboard__chart-title-group"]}
                    >
                      <h3 className={styles["admin-dashboard__chart-title"]}>
                        <PieChart size={20} strokeWidth={2.5} />
                        Category-wise Usage
                      </h3>
                      <p className={styles["admin-dashboard__chart-subtitle"]}>
                        Distribution by API category
                      </p>
                    </div>
                  </div>
                  {graphLoading ? (
                    <PieSkeleton />
                  ) : (
                    <PieChartComponent pieGraphData={pieGraphData} />
                  )}
                </div>
              )}
            </div>
          )}

          {/* TABLE SECTION */}
          {(loading || rows.length > 0) && (
            <div className={styles["admin-dashboard__card"]}>
              <div className={styles["admin-dashboard__table-header"]}>
                <h3 className={styles["admin-dashboard__table-title"]}>
                  API Requests
                </h3>
              </div>

              <div className={styles["admin-dashboard__controls"]}>
                <div className={styles["admin-dashboard__showing-text"]}>
                  Showing {rows.length} of {pagination.total}
                </div>

                <div className={styles["admin-dashboard__actions"]}>
                  <DateRangePicker
                    key={datePickerKey}
                    maxDate={new Date()}
                    onChange={(type, value) => {
                      if (type === "from") {
                        setFromDate(value);
                        setPage(1);
                      }
                      if (type === "to") {
                        setToDate(value);
                        setPage(1);
                      }
                    }}
                  />

                  <button
                    className={styles["admin-dashboard__download-btn"]}
                    onClick={handleDownload}
                  >
                    <Download size={16} strokeWidth={2.5} />
                    Download
                  </button>
                </div>
              </div>

              {loading ? (
                <TableSkeleton columns={5} rows={6} />
              ) : (
                <>
                  <APITable columns={columns} data={rows} />

                  <Pagination
                    currentPage={page}
                    totalPages={pagination.totalPages}
                    rowsPerPage={limit}
                    onPageChange={setPage}
                    onRowsPerPageChange={(l) => {
                      setLimit(l);
                      setPage(1);
                    }}
                  />
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
